import React from 'react';
import { useQuery, useMutation } from '@apollo/react-hooks';
import PropTypes from 'prop-types';
import { navigate } from '@reach/router';

import AudioPlayer from '../components/AudioPlayer';
import Layout from '../components/Layout';
import Container from '../components/Container';
import Notification from '../components/Notification';
import TextContent from '../components/TextContent';
import Section from '../components/Section';
import FooterBar from '../components/FooterBar';
import Grid from '../components/Grid';
import GridItem from '../components/GridItem';
import Table from '../components/Table';
import Tag from '../components/Tag';

import { GET_ACTIVITY_DETAILS, UPDATE_ACTIVITY } from '../graph/activities';
import { GET_CMS_CONTENT } from '../graph/cms';
import {
  GET_HASHTAGS,
  ADD_HASHTAG,
  DELETE_HASHTAG,
  UPDATE_HASHTAG,
} from '../graph/hashtags';

import extractGraphQLError from '../utils/extractGraphQLError';
import sleep from '../utils/sleep';
import {
  extractPrismicString,
  extractPrismicContent,
  hasContent,
} from '../utils/prismic';
import { validate, requiredString } from '../utils/validate';
import useTrackActivityGA from '../utils/useTrackActivityGA';

import { activities, ACTIVITY_STATUS } from '../config/levels';

import { ReactComponent as RemoveIcon } from '../assets/remove-icon.svg';

const formValidationSchema = validate({
  title: requiredString,
  description1: requiredString,
});

const formInitialValues = {
  title: '',
  description1: '',
};

const formFields = [
  {
    id: 'title',
    name: 'title',
    label: 'Career Interest',
    type: 'textarea',
    placeholder: 'Enter career interest...',
  },
  {
    id: 'description1',
    name: 'description1',
    label: 'Description',
    type: 'textarea',
    placeholder: 'Enter a description...',
  },
];

function ActivityHashtags({ activityId }) {
  const { data: cmsData } = useQuery(GET_CMS_CONTENT, {
    fetchPolicy: 'cache-only',
  });

  const { data, loading } = useQuery(GET_ACTIVITY_DETAILS, {
    variables: { id: activityId },
  });

  const { loading: loadingHashtags, data: dataHashtags } = useQuery(
    GET_HASHTAGS,
    {
      variables: { activityId },
    },
  );

  const initialHashtags = dataHashtags?.hashtags?.filter(
    ({ type }) => type === 'INITIAL',
  );

  const finalHashtags = dataHashtags?.hashtags?.filter(
    ({ type }) => type === 'FINAL',
  );

  const isHashtagValid = finalHashtags?.length > 2;

  const [
    handleAddHashtag,
    { loading: loadingAddHashtag, error: errorAddHashtag },
  ] = useMutation(ADD_HASHTAG, {
    refetchQueries: ['Hashtags'],
  });

  const handleNewHashtag = formData => {
    handleAddHashtag({
      variables: {
        activityId,
        type: 'FINAL',
        ...formData,
      },
    });
  };

  const [
    handleDeleteHashtag,
    { loading: loadingDeleteHashtag, error: errorDeleteHashtag },
  ] = useMutation(DELETE_HASHTAG, {
    refetchQueries: ['Hashtags'],
  });

  const [
    handleUpdateActivity,
    { loading: loadingUpdateActivity, error },
  ] = useMutation(UPDATE_ACTIVITY);

  const [
    handleUpdateHashtag,
    { loading: loadingUpdateHashtag, error: errorUpdateHashtag },
  ] = useMutation(UPDATE_HASHTAG, {
    refetchQueries: ['Interests'],
  });

  const activityType = data?.activity?.type;

  useTrackActivityGA(data?.activity?.type, '1');

  const { level } = activities[activityType] || {};

  const {
    title,
    sub_title: subTitle,
    audio_instructions_3: audioInstructions,
    help_text_3: helpText,
    short_instructions_3: shortInstructions,
    step_label_3: stepInstructions,
  } =
    cmsData?.cmsContent?.activity?.find(
      activity =>
        activity.id ===
        (cmsData?.user?.currentLevel === 4 &&
        data?.activity?.status === ACTIVITY_STATUS.COMPLETED
          ? `ongoing_${activityType}`
          : activityType),
    ) ||
    cmsData?.cmsContent?.activity?.find(
      activity => activity.id === activityType,
    ) ||
    {};

  const handleCompleteActivity = async () => {
    await handleUpdateActivity({
      variables: {
        id: activityId,
        status: ACTIVITY_STATUS.COMPLETED,
      },
    });

    await sleep(500);

    navigate(`/activities/${activityId}/completed`);
  };

  return (
    <Layout
      title={`${extractPrismicString(title)}`}
      loading={loading || loadingHashtags}
      hasFooterBar
    >
      <Container>
        <Section>
          {!!(hasContent(shortInstructions) || hasContent(helpText)) && (
            <Grid>
              <GridItem width={60}>
                <TextContent redh6 smallMargin>
                  {extractPrismicContent(shortInstructions)}
                </TextContent>
              </GridItem>

              <GridItem width={40}>
                {!!(audioInstructions?.url || hasContent(helpText)) && (
                  <Notification showUser noMarginBottom>
                    {extractPrismicContent(helpText)}

                    {!!audioInstructions?.url && (
                      <AudioPlayer fileUrl={audioInstructions?.url} />
                    )}
                  </Notification>
                )}
              </GridItem>
            </Grid>
          )}
        </Section>

        <TextContent redh6 smallMargin>
          <h6>Step 3</h6>
          <p>{extractPrismicString(stepInstructions)}</p>
        </TextContent>

        {initialHashtags?.map(({ id, ...item }) => (
          <Tag key={id}>{item.title}</Tag>
        ))}

        <Table
          cols={[
            { id: 'title', value: 'Career Interest' },
            { id: 'description1', value: 'Description' },
          ]}
          data={finalHashtags}
          actions={[
            {
              id: 'remove',
              text: 'remove',
              icon: <RemoveIcon />,
              onClick: handleDeleteHashtag,
              loading: loadingDeleteHashtag,
            },
          ]}
          form={{
            fields: formFields,
            loading: loadingAddHashtag,
            validationSchema: formValidationSchema,
            initialValues: formInitialValues,
            onSubmit: handleNewHashtag,
          }}
          editable
          updateForm={{
            fields: formFields,
            loading: loadingUpdateHashtag,
            validationSchema: formValidationSchema,
            initialValues: formInitialValues,
            onSubmit: handleUpdateHashtag,
          }}
        />

        {!!(
          error ||
          errorAddHashtag ||
          errorDeleteHashtag ||
          errorUpdateHashtag
        ) && (
          <Notification type="red">
            {extractGraphQLError(
              error ||
                errorAddHashtag ||
                errorDeleteHashtag ||
                errorUpdateHashtag,
            )}
          </Notification>
        )}
      </Container>

      <FooterBar
        title={extractPrismicString(title)}
        subTitle={extractPrismicString(subTitle)}
        primaryButton={{
          label: 'Complete Activity',
          onClick: handleCompleteActivity,
          loading: loadingUpdateActivity,
          disabled: !isHashtagValid,
          level,
        }}
        secondaryButton={{
          label: 'Back to interests',
          to: `/activities/${activityId}/interests`,
        }}
      />
    </Layout>
  );
}

ActivityHashtags.propTypes = {
  activityId: PropTypes.string,
};

ActivityHashtags.defaultProps = {
  activityId: null,
};

export default ActivityHashtags;
